import { useState } from 'react'
import { api, ApiRequestError } from '../../lib/api'
import { useAuthStore, type UiVersion } from '../../stores/authStore'

/**
 * Admin › Users: the UI column and the filter (redesign F2; spec f2_ui_version_shell.md §9).
 * The admin pages keep their current design.
 */

export type UiVersionFilterValue = 'all' | UiVersion

const FILTERS: { value: UiVersionFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'old', label: 'Old UI' },
  { value: 'new', label: 'New UI' },
]

export function UiVersionFilter({
  value,
  onChange,
}: {
  value: UiVersionFilterValue
  onChange: (value: UiVersionFilterValue) => void
}) {
  return (
    <div role="group" aria-label="Filter by UI version" className="flex gap-1">
      {FILTERS.map((f) => (
        <button
          key={f.value}
          type="button"
          aria-pressed={value === f.value}
          onClick={() => onChange(f.value)}
          className={
            value === f.value
              ? 'text-xs px-3 py-1.5 rounded-lg bg-primary-container text-on-primary font-medium'
              : 'text-xs px-3 py-1.5 rounded-lg text-secondary hover:bg-surface-container-high'
          }
        >
          {f.label}
        </button>
      ))}
    </div>
  )
}

const LABEL: Record<UiVersion, string> = { old: 'Old', new: 'New' }

interface SwitchResponse {
  copied?: { applications_created: number; links_added: number }
}

/** A failed switch, in full. For a failed copy (F3) that includes which step, session or CV
 * failed and why (spec f3_applications_copy.md §8). */
interface SwitchError {
  message: string
  lines: [string, string][]
}

const DETAIL_LABELS: [string, string][] = [
  ['step', 'Step'],
  ['session_id', 'Session'],
  ['cv_record_id', 'CV'],
  ['error_type', 'Error type'],
  ['reason', 'Reason'],
]

function toSwitchError(err: unknown): SwitchError {
  if (!(err instanceof ApiRequestError)) return { message: 'Switch failed', lines: [] }
  const details = err.error.details
  const lines: [string, string][] = []
  if (details && typeof details === 'object') {
    const record = details as Record<string, unknown>
    for (const [key, label] of DETAIL_LABELS) {
      const value = record[key]
      if (value !== null && value !== undefined && value !== '') lines.push([label, String(value)])
    }
  }
  return { message: err.error.message, lines }
}

function copiedNote(res: SwitchResponse | undefined): string | null {
  const n = res?.copied?.applications_created
  if (n === undefined) return null
  return `Copied: ${n} application${n === 1 ? '' : 's'}`
}

/** One user's UI version, with a button to switch it. */
export function UiVersionCell({
  userId,
  version,
  onChanged,
}: {
  userId: string
  version: UiVersion
  onChanged: (userId: string, version: UiVersion) => void
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<SwitchError | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const ownId = useAuthStore((s) => s.user?.id)
  const fetchUser = useAuthStore((s) => s.fetchUser)
  const target: UiVersion = version === 'new' ? 'old' : 'new'

  const handleSwitch = async () => {
    setSaving(true)
    setError(null)
    setNote(null)
    try {
      const res = await api.put<SwitchResponse>(`/api/v1/admin/users/${userId}/ui_version`, {
        ui_version: target,
      })
      onChanged(userId, target)
      setNote(copiedNote(res))
      // Switching your own account: refresh /user/me so the next page uses the new version.
      if (userId === ownId) await fetchUser()
    } catch (err) {
      setError(toSwitchError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3">
        <span className="text-sm text-on-surface">{LABEL[version]}</span>
        <button
          type="button"
          onClick={handleSwitch}
          disabled={saving}
          className="text-xs text-primary-container hover:underline font-medium disabled:opacity-40"
        >
          {saving ? '…' : `Switch to ${LABEL[target]}`}
        </button>
      </div>
      {note && <span className="text-xs text-secondary">{note}</span>}
      {error && (
        <div role="alert" className="text-xs text-error flex flex-col gap-0.5 max-w-md">
          <span className="font-medium">{error.message}</span>
          {error.lines.map(([label, value]) => (
            <span key={label} className="break-all">
              {label}: {value}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
